import { useCallback, useEffect, useMemo } from "react";
import { useTranslation } from "react-i18next";
import moment from "moment-timezone";
import { faCalendar } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { InputGroup, InputGroupAddon, InputGroupText } from "reactstrap";
import ResourceSelect from "../../../../../../components/Inputs/ResourceSelect";
import DateInput from "../../../../../../components/Inputs/DateInput";
import RangeDateInput from "../../../../../../components/Inputs/DateInput/Range";
import Select from "../../../../../../components/Inputs/Select";
import {
    getStartDate,
    getEndDateFromStart
} from "../../../../../../components/DateRangeFilters/helpers";
import {
    getStartDateFromEndDate,
    getEndDateFromStartDate
} from "../../../../../Settings/PayPolicies/PayGroup/Form/helpers";
import {
    renderSettingsResourceLabel,
    getSettingsResourceSearchFilters
} from "../../../../../../utils/helpers/settings";
import { usePayGroups } from "../../../../../../utils/api/payGroup";

const DateFilters = ({ filters, setFilters }) => {
    const { t } = useTranslation();

    const { mode, from, to, payGroup, payGroupPeriod } = filters;

    const {
        data: payGroups,
        hasFetched: hasFetchedPayGroups,
        fetch: fetchPayGroups,
    } = usePayGroups();

    const onDateChange = useCallback((val) => {
        if (!mode) {
            return;
        }
        const value = moment(val);
        const data = {
            from: getStartDate(value, mode),
        };
        if (mode !== "custom") {
            data.to = getEndDateFromStart(value, mode);
        }
        setFilters((prev) => ({ ...(prev || {}), ...data }));
    }, [mode, setFilters]);

    const onWeekChange = useCallback(([value]) => {
        onDateChange(value);
    }, [onDateChange]);

    const onRangeChange = useCallback(([from, to]) => {
        setFilters((prev) => ({
            ...(prev || {}),
            from: moment(from),
            to: moment(to)
        }));
    }, [setFilters]);

    const onPayGroupChange = useCallback(({ code }) => {
        setFilters((prev) => ({
            ...(prev || {}),
            payGroup: code
        }));
    }, [setFilters]);

    const onPeriodChange = useCallback((value) => {
        setFilters((prev) => ({
            ...(prev || {}),
            payGroupPeriod: value
        }));
    }, [setFilters]);

    const onModeChange = useCallback((mode) => {
        let from;
        let to;
        let payGroup;
        let payGroupPeriod;
        switch (mode) {
            case "date": {
                from = moment().toISOString(true);
                to = moment().toISOString(true);
                break;
            }
            case "week": {
                from = moment().startOf("week").toISOString(true);
                to = moment().endOf("week").toISOString(true);
                break;
            }
            case "month": {
                from = moment().startOf("month").toISOString(true);
                to = moment().endOf("month").toISOString(true);
                break;
            }
            case "custom": {
                from = undefined;
                to = undefined;
                break;
            }
            case "pay-group": {
                from = undefined;
                to = undefined;
                payGroup = undefined;
                payGroupPeriod = "current";
                break;
            }
            default: {
                from = undefined;
                to = undefined;
                break;
            }
        }
        setFilters({ mode, from, to, payGroup, payGroupPeriod });
    }, [setFilters]);

    useEffect(() => {
        const controller = new AbortController();
        fetchPayGroups({
            params: { pagination: "false" },
            signal: controller.signal,
        });
        return () => controller.abort();
    }, [fetchPayGroups]);

    const selectedPayGroup = useMemo(() => {
        return payGroups?.find(({ code }) => code === payGroup);
    }, [payGroup, payGroups]);

    useEffect(() => {
        if (hasFetchedPayGroups) {
            if (mode === "pay-group") {
                if (selectedPayGroup && payGroupPeriod) {
                    let { frequency, periodStartDate, periodEndDate } = selectedPayGroup;
                    periodStartDate = moment
                        .parseZone(periodStartDate)
                        .toISOString(true);
                    periodEndDate = moment
                        .parseZone(periodEndDate)
                        .toISOString(true);
                    let period = payGroupPeriod;
                    let from, to;
                    switch (payGroupPeriod) {
                        case "past": {
                            to = moment
                                .parseZone(periodStartDate)
                                .subtract(1, "day")
                                .toISOString(true);
                            from = getStartDateFromEndDate(
                                moment.parseZone(to),
                                frequency,
                            ).toISOString(true);
                            break;
                        }
                        case "current": {
                            from = periodStartDate;
                            to = periodEndDate;
                            break;
                        }
                        case "next": {
                            from = moment
                                .parseZone(periodEndDate)
                                .add(1, "day")
                                .toISOString(true);
                            to = getEndDateFromStartDate(
                                from,
                                frequency,
                            ).toISOString(true);
                            break;
                        }
                        default: {
                            period = "current";
                            from = periodStartDate;
                            to = periodEndDate;
                            break;
                        }
                    }
                    setFilters((prev) => ({
                        ...prev,
                        payGroupPeriod: period,
                        from,
                        to
                    }));
                }
            } else {
                if (selectedPayGroup || payGroupPeriod) {
                    setFilters({
                        payGroup: null,
                        payGroupPeriod: null,
                    });
                }
            }
        }
    }, [hasFetchedPayGroups, mode, payGroupPeriod, selectedPayGroup, setFilters]);

    return (
        <div className="d-flex align-items-center mb-3">
            <Select
                placeholder={t("mode")}
                value={mode}
                onChange={onModeChange}
                style={{ width: "200px", marginRight: "4px" }}
                showSearch
            >
                <Select.Option value="date">{t("date")}</Select.Option>
                <Select.Option value="week">{t("week")}</Select.Option>
                <Select.Option value="month">{t("month")}</Select.Option>
                <Select.Option value="custom">{t("custom")}</Select.Option>
                <Select.Option value="pay-group">
                    {t("pay-group")}
                </Select.Option>
            </Select>

            {mode === "pay-group" ? (
                <div className="d-flex">
                    <ResourceSelect
                        resourcePath="/pay-group?pagination=off"
                        renderLabel={renderSettingsResourceLabel}
                        hasSearch
                        getSearchFilters={getSettingsResourceSearchFilters}
                        onChange={onPayGroupChange}
                        value={payGroup}
                        style={{ width: "250px" }}
                    />

                    <Select
                        className="ml-1"
                        value={payGroupPeriod}
                        onChange={onPeriodChange}
                        style={{ width: "250px" }}
                    >
                        <Select.Option value="past">{t("past")}</Select.Option>
                        <Select.Option value="current">
                            {t("current")}
                        </Select.Option>
                        <Select.Option value="next">{t("next")}</Select.Option>
                    </Select>
                </div>
            ) : (
                <InputGroup
                    className="input-group-alternative border shadow-none flex-nowrap ml-1"
                    style={{ width: "250px" }}
                >
                    {mode === "custom" ? (
                        <RangeDateInput
                            startDate={moment(from).toDate()}
                            endDate={moment(to).toDate()}
                            onChange={onRangeChange}
                        />
                    ) : mode === "week" ? (
                        <DateInput
                            startDate={moment(from).toDate()}
                            endDate={moment(to).toDate()}
                            onChange={onWeekChange}
                            selectsRange
                        />
                    ) : (
                        <DateInput
                            value={moment(from).toDate()}
                            showMonthYearPicker={mode === "month"}
                            dateFormat={
                                mode === "month" ? "yyyy-MM" : undefined
                            }
                            onChange={onDateChange}
                        />
                    )}
                    <InputGroupAddon addonType="append">
                        <InputGroupText>
                            <FontAwesomeIcon icon={faCalendar} />
                        </InputGroupText>
                    </InputGroupAddon>
                </InputGroup>
            )}
        </div>
    );
};

export default DateFilters;
